const CAPACITY_ENDPOINT =
  process.env.REACT_APP_BONUS_REGISTRATION_CAPACITY_URL ||
  process.env.REACT_APP_DATA_JSON_URL || "/json/wlp/fgh/json.jsp"

const COOLDOWN_MS = 10 * 60 * 1000
export function getWelcomeBonusInitialSlots() {
  if (process.env.NODE_ENV === "development" && typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("bonusInitialSlots") === "2") {
    return 2
  }
  return 50
}
const phDay = (timestamp) => new Date(timestamp + 8 * 60 * 60 * 1000).toISOString().slice(0, 10)

const FULL_STATUS_VALUES = new Set([
  "full",
  "paused",
  "queued",
  "waitlist",
  "capacity_full",
  "bonus_capacity_full",
])

const asFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const parseDate = (value) => {
  if (!value) return null

  // JSP dates without an offset are Philippine local time, independent of the browser.
  const normalized = typeof value === "string" ? value.replace(" ", "T") : value
  const timestamp = new Date(
    typeof normalized === "string" && /^\d{4}-\d{2}-\d{2}T/.test(normalized) &&
      !/(Z|[+-]\d{2}:?\d{2})$/i.test(normalized)
      ? `${normalized}+08:00` : normalized
  ).getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

export function normalizeWelcomeBonusCapacity(payload = {}, now = Date.now()) {
  const data =
    payload?.data && typeof payload.data === "object" ? payload.data : payload

  if (data && Object.prototype.hasOwnProperty.call(data, "count")) {
    const initialSlots = getWelcomeBonusInitialSlots()
    const count = asFiniteNumber(data.count)
    if (count === null || !Number.isInteger(count) || count < 0) {
      throw new Error("Invalid registration count")
    }
    const lastRegistration = parseDate(data.last_reg_date)
    if (count >= initialSlots && lastRegistration === null) {
      throw new Error("Missing or invalid last registration date")
    }
    if (count === 0 || (lastRegistration !== null && phDay(lastRegistration) < phDay(now))) {
      return { available: true, remainingSlots: initialSlots, nextAvailableAt: null }
    }
    if (count < initialSlots) {
      return { available: true, remainingSlots: initialSlots - count, nextAvailableAt: null }
    }
    // Only a completed batch starts a cooldown: 50, 60, 70, ... .
    // Registrations within the released batch must not restart its timer.
    const batchCount = (count - initialSlots) % 10
    const midnight = Date.parse(`${phDay(now)}T00:00:00+08:00`) + 86400000
    const releaseTime = Math.min(lastRegistration + COOLDOWN_MS, midnight)
    const isFull = batchCount === 0 && now < releaseTime
    return {
      available: !isFull,
      remainingSlots: isFull ? 0 : 10 - batchCount,
      nextAvailableAt: isFull ? releaseTime : null,
    }
  }

  const status = String(data?.status || data?.code || "").toLowerCase()

  const remainingSlots = asFiniteNumber(
    data?.remainingSlots ?? data?.remaining_slots ?? data?.slotsRemaining
  )

  const secondsUntilNextBatch = asFiniteNumber(
    data?.secondsUntilNextBatch ??
      data?.seconds_until_next_batch ??
      data?.retryAfterSeconds ??
      data?.retry_after
  )

  const nextAvailableAt =
    parseDate(
      data?.nextAvailableAt ??
        data?.next_available_at ??
        data?.nextBatchAt ??
        data?.next_batch_at
    ) ||
    (secondsUntilNextBatch !== null
      ? now + Math.max(0, secondsUntilNextBatch) * 1000
      : null)

  const isFull =
    data?.available === false ||
    data?.isFull === true ||
    data?.is_full === true ||
    data?.bonusRegistrationPaused === true ||
    data?.bonus_registration_paused === true ||
    remainingSlots === 0 ||
    FULL_STATUS_VALUES.has(status)

  return {
    available: !isFull,
    remainingSlots,
    nextAvailableAt,
  }
}

function withLocalCountdownTest(data) {
  const initialSlots = getWelcomeBonusInitialSlots()
  if (process.env.NODE_ENV !== "development" || typeof window === "undefined" ||
      (initialSlots !== 2 && new URLSearchParams(window.location.search).get("bonusCountdownTest") !== "1") ||
      Number(data.count) < initialSlots || data.last_reg_date != null) return data

  // Explicit development-only workaround for the backend's count-only fixture.
  // Persist the start across requests/reloads so the countdown can actually finish.
  const key = `fgh-bonus-countdown-test:${initialSlots}:${data.count}`
  let startedAt = Number(window.sessionStorage.getItem(key))
  if (!Number.isFinite(startedAt) || startedAt <= 0) {
    startedAt = Date.now()
    window.sessionStorage.setItem(key, String(startedAt))
  }
  const testData = { ...data, last_reg_date: new Date(startedAt).toISOString() }
  console.info("[Welcome bonus] LOCAL TEST timestamp (not supplied by API):", testData.last_reg_date)
  return testData
}

export async function getWelcomeBonusCapacity({ signal } = {}) {
  console.info("[Welcome bonus] Checking registration slots...")
  // This JSP command requires form-encoded POST, like the other data commands.
  const response = await fetch(CAPACITY_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ cmd: "getRegistrationCount" }),
    signal,
    cache: "no-store",
  })
  if (!response.ok) {
    console.error("[Welcome bonus] API request failed:", response.status)
    throw new Error("Unable to check welcome bonus availability")
  }
  const payload = await response.json()
  console.info("[Welcome bonus] API response:", JSON.stringify(payload))
  const data = payload?.data ?? payload
  if (!data || !Object.prototype.hasOwnProperty.call(data, "count")) {
    console.error("[Welcome bonus] API did not return a registration count.")
    throw new Error("Registration-count response is missing count")
  }
  const capacity = normalizeWelcomeBonusCapacity(withLocalCountdownTest(data))
  const secondsUntilNextBatch = capacity.nextAvailableAt === null
    ? 0 : Math.max(0, Math.ceil((capacity.nextAvailableAt - Date.now()) / 1000))
  const countdown = `${String(Math.floor(secondsUntilNextBatch / 60)).padStart(2, "0")}:${String(secondsUntilNextBatch % 60).padStart(2, "0")}`
  // Log a snapshot so DevTools shows the values at the time of this check.
  // Availability and cooldown are derived here; the API returns count/date only.
  console.info("[Welcome bonus] Calculated availability:", JSON.stringify({
    available: capacity.available,
    remainingSlots: capacity.remainingSlots,
    secondsUntilNextBatch,
    countdown,
    nextAvailableAt: capacity.nextAvailableAt === null
      ? null : new Date(capacity.nextAvailableAt).toISOString(),
  }))
  return capacity
}

export function emitWelcomeBonusCapacityFull(payload = {}) {
  const capacity = normalizeWelcomeBonusCapacity(payload)

  window.dispatchEvent(
    new CustomEvent("fgh:bonus-capacity-full", {
      detail: capacity,
    })
  )
}
